// Single-node planner discovery — headless driver.
//
// Drives ONE "build this node" conversation against the planner discovery
// stream (singleNode mode: probing + Q&A) without owning any UI. Extracted
// from AiFlowChatPanel's stream handling so the Workflow Copilot can run the
// same discovery inside its own chat surface (no nested mini-chat).
//
// createSingleNodeDiscovery({
//   initialPrompt,      — the node spec to build from (sent as the first turn)
//   agentMode,          — hint: build as an agent node (chat()) vs deterministic
//   responseFields,     — agent response field hints
//   singleNodeContext,  — source-node snapshot (buildSingleNodeContextForSource)
//   onState(state)      — render callback, fired on every stream event:
//                         { phase: 'thinking'|'streaming'|'probing'|'awaiting',
//                           thinkingStatus, streamText,
//                           probeEvents: [{label,status,probeType,output}],
//                           question: {display, raw, parts}|null }
// }) → {
//   result,             — Promise<{prompt, agentMode, responseFields}>; rejects
//                         on stop/error/stall
//   answer(text),       — continue the conversation (only while 'awaiting')
//   stop(reason)        — abort the stream and reject `result`
// }

var { streamChatWorkflowPlan } = require('./workflowStorage');
var { parseAiMessage, isPrematureStop } = require('./plannerChatFormat');

// A turn that signals <ready/> but emits no <node> (or trails off) leaves
// nothing to answer and no node to build — nudge the model with "continue",
// capped so a stuck model can't loop forever. Mirrors AiFlowChatPanel's guard.
var MAX_AUTO_CONTINUE = 3;

function createSingleNodeDiscovery(opts) {
  var o = opts || {};
  var onState = typeof o.onState === 'function' ? o.onState : function() {};

  var history = [];
  var abortFn = null;
  var autoContinues = 0;
  var settled = false;
  var resolveSpec = null;
  var rejectSpec = null;
  var result = new Promise(function(res, rej) { resolveSpec = res; rejectSpec = rej; });
  var chatSessionId = Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 8);
  var state = { phase: 'thinking', thinkingStatus: '', streamText: '', probeEvents: [], question: null };

  function emit(patch) {
    state = Object.assign({}, state, patch);
    onState(state);
  }

  function settle(fn, value) {
    if (settled) return;
    settled = true;
    if (abortFn) { try { abortFn(); } catch (e) {} abortFn = null; }
    fn(value);
  }

  function sendTurn(text, isAuto) {
    if (settled || !text) return;
    // A real user answer resets the auto-continue budget; our own injected
    // "continue" must not, or the cap would never bite.
    if (!isAuto) autoContinues = 0;
    history.push({ role: 'user', content: text });
    emit({ phase: 'thinking', thinkingStatus: '', streamText: '', probeEvents: [], question: null });
    if (abortFn) { try { abortFn(); } catch (e) {} }
    abortFn = streamChatWorkflowPlan(
      history.map(function(m) { return { role: m.role, content: m.content }; }),
      null,
      {
        onEvent: handleEvent,
        onError: function(err) {
          settle(rejectSpec, err instanceof Error ? err : new Error('Node discovery failed.'));
        }
      },
      // Single-node discovery always runs the planner 'balance' tier (Sonnet),
      // same as the balloon — never Haiku/Opus.
      { mode: 'balance', chatSessionId: chatSessionId, singleNode: true, singleNodeContext: o.singleNodeContext || null }
    );
  }

  function handleEvent(evt) {
    if (settled || !evt) return;
    if (evt.type === 'thinking') {
      emit({ phase: 'thinking', thinkingStatus: evt.text || 'Thinking…' });
    } else if (evt.type === 'chunk_start') {
      emit({ phase: 'streaming', thinkingStatus: '', streamText: '' });
    } else if (evt.type === 'chunk') {
      emit({ phase: 'streaming', streamText: evt.fullText || '' });
    } else if (evt.type === 'probes_start') {
      emit({ phase: 'probing', streamText: '', probeEvents: [] });
    } else if (evt.type === 'probe_running') {
      emit({ phase: 'probing', probeEvents: state.probeEvents.concat([{ label: evt.label, status: 'running', probeType: evt.probeType }]) });
    } else if (evt.type === 'probe_result') {
      emit({
        probeEvents: state.probeEvents.map(function(pe) {
          return (pe.label === evt.label && pe.status === 'running')
            ? { label: evt.label, status: evt.status, probeType: evt.probeType, output: evt.output }
            : pe;
        })
      });
    } else if (evt.type === 'done') {
      handleDone(evt);
    } else if (evt.type === 'error') {
      settle(rejectSpec, new Error(evt.message || 'Node discovery failed.'));
    }
  }

  function handleDone(evt) {
    abortFn = null;
    // Node spec ready — discovery is finished.
    if (evt.singleNode && evt.singleNode.prompt) {
      settle(resolveSpec, evt.singleNode);
      return;
    }
    var raw = evt.rawMessage || evt.message || '';
    var display = evt.message || raw;
    var parsed = parseAiMessage(display);
    var hasVisible = parsed.parts.some(function(p) { return p.type !== 'text' || (p.text && p.text.trim()); });
    var awaitsUser = parsed.parts.some(function(p) { return p.type === 'question' || p.type === 'input'; });
    if (raw) history.push({ role: 'assistant', content: raw });

    // A question — or a complete prose turn the user can reply to — waits here.
    if (awaitsUser || (hasVisible && !isPrematureStop(raw))) {
      emit({ phase: 'awaiting', thinkingStatus: '', streamText: '', probeEvents: [], question: { display: display, raw: raw, parts: parsed.parts } });
      return;
    }
    // Bare <ready/> with no node, or a trailed-off turn — auto-nudge (capped).
    if (autoContinues < MAX_AUTO_CONTINUE) {
      autoContinues += 1;
      sendTurn('continue', true);
      return;
    }
    settle(rejectSpec, new Error('The node builder stalled before finishing the node.'));
  }

  function answer(text) {
    var t = String(text || '').trim();
    if (!t || settled || state.phase !== 'awaiting') return;
    sendTurn(t, false);
  }

  function stop(reason) {
    settle(rejectSpec, new Error(reason || 'Node creation was cancelled.'));
  }

  // Kick off with the caller's spec + the same node-type hint the balloon sends.
  var hint;
  if (o.agentMode) {
    var fields = (o.responseFields || []).filter(function(f) { return String(f || '').trim(); });
    hint = '\n\n(Build this as an AI agent node that uses chat()'
      + (fields.length ? ', returning fields: ' + fields.join(', ') : '') + '.)';
  } else {
    hint = '\n\n(Build this as a deterministic custom code node.)';
  }
  sendTurn(String(o.initialPrompt || '').trim() + hint, false);

  return { result: result, answer: answer, stop: stop };
}

module.exports = { createSingleNodeDiscovery: createSingleNodeDiscovery };
